import { MethodInfo, RpcStatus } from '@protobuf-ts/runtime-rpc'
import { GenericError } from './k2-error'
import { Info, Status } from 'shared/gen/pkg/proto/response'
import { processK2Response } from 'shared/services/grpc/client-utils'
import {
  GuaranteedResponse,
  RequiredK2Response,
} from 'shared/services/grpc/k2-use-case'

/**
 * These utilities can be used in conjunction with the K2GenericError to generate
 * a response that can be used to create a K2GenericError
 */

const notOKStatus: RpcStatus = {
  code: 'UNKNOWN',
  detail: 'message from the status.detail field',
}

const okStatus: RpcStatus = {
  code: 'OK',
  detail: '',
}

const unhandledInfo: Info = {
  status: Status.ERROR,
  message: '',
}

const successInfo: Info = {
  status: Status.SUCCESS,
  message: 'Success',
}

/**
 * @deprecated # use `generateK2ServerActionErrorResponse` instead
 * generates a handled k2 response, where the response.info.message is not set and the status is not Status.SUCCESS and the status.code is not OK
 */
export function generateHandledK2ErrorResponse(
  message: string,
): RequiredK2Response<object> {
  if (message === '') {
    throw new Error('message is required in order to create a handled K2 error')
  }

  return generateK2Response(
    {
      info: {
        ...unhandledInfo,
        message: message,
      },
    },
    notOKStatus,
  )
}

/**
 * @deprecated # use `generateProcessedServerActionErrorResponse` instead
 * generates an unhandled k2 response, where the response.info.message is not set and the status is not Status.SUCCESS and the status.code is not OK
 * - no message option here is intended, because we're using this as an "unknown internal error" from k2
 */
export function generateUnhandledK2ErrorResponse(): RequiredK2Response<object> {
  return generateK2Response(
    {
      info: unhandledInfo,
    },
    notOKStatus,
  )
}

/**
 * @deprecated # use `generateProcessedServerActionErrorResponse` instead
 *
 * This is a convenience method to generate a K2GenericError from a handled K2ErrorResponse
 * - Will be mostly used in rejecting errors from the grpc-interface layer
 */
export function generateHandledK2GenericError(message: string): GenericError {
  return new GenericError(generateHandledK2ErrorResponse(message))
}

/**
 * @deprecated # use `generateProcessedServerActionErrorResponse` instead
 *
 * This is a convenience method to generate a K2GenericError from an unhandled K2ErrorResponse
 * - Will be mostly used in rejecting errors from the grpc-interface layer
 */
export function generateUnhandledK2GenericError(): GenericError {
  return new GenericError(generateUnhandledK2ErrorResponse())
}

/**
 * @deprecated # use `generateProcessedServerActionSuccessResponse` instead
 *
 * generates a successful k2 response, allowing for the input to override the default values
 * - if you're getting an error using this during a mocked grpc-layer success response, you might need to use the processK2Response method to get the correct type
 */
export function generateSuccessK2Response<T extends object>(
  // the response data. Don't include 'info', or it will override the success response shape
  response?: T,
  // the message included in the response.info.message field
  message?: string,
): RequiredK2Response<GuaranteedResponse<T>> {
  return generateK2Response<T>(
    {
      info: {
        ...successInfo,
        message: message ?? successInfo.message,
      },
      ...response,
    } as T,
    okStatus,
  ) as RequiredK2Response<GuaranteedResponse<T>>
}

/**
 * @deprecated # use `generateProcessedServerActionResponse` instead
 *
 * This is a convenient method to mock a successful k2 response from the grpc-layer
 * - It's a wrapper around generateSuccessK2Response that processes the response, so we get the expected type
 * - This is what most of our mocks of the grpc-layer should be using for successful responses
 */
export function generateProcessedSuccessK2Response<T extends object>(
  response?: T | null,
  message?: string,
): GuaranteedResponse<T> {
  return processK2Response(
    generateSuccessK2Response<T>(response || undefined, message),
  )
}

/**
 * @deprecated # use `generateProcessedServerActionResponse` instead
 *
 * A generic method to generate a K2 response, allowing for the input to override the default values
 */
function generateK2Response<T extends object>(
  response: T,
  status?: Partial<RpcStatus>,
): RequiredK2Response<T> {
  const method: Pick<MethodInfo, 'name' | 'service'> = {
    name: 'test',
    service: {
      typeName: 'test',
      methods: [],
      options: {},
    },
  }

  return {
    // casting this to unknown to avoid type errors, but we've got partial type safety above
    method: method as unknown as MethodInfo,

    status: {
      ...okStatus,
      ...status,
    },
    response: {
      info: {
        ...successInfo,
      },
      ...response,
    },
  }
}
